import axios from 'axios';

// Fallback port matches the backend default (5000) — was incorrectly 5001
// which silently broke local dev when the env var wasn't set.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export interface EnrollmentInfoDTO {
  enrollmentId: string;
  sessionId: string;
  sessionName: string;
  classId: string;
  className: string;
  sectionInstanceId: string | null;
  sectionName: string | null;
  rollNumber: number;
  status: string;
}

export interface StudentReportData {
  student: {
    _id: string;
    name: string;
    rollNumber: string;
    phone: string;
    fatherName: string;
    fatherPhone: string;
    contact: string;
    address: string;
    isActive: boolean;
    enrollmentInfo: EnrollmentInfoDTO | null;
    centerInfo: {
      _id: string;
      name: string;
    } | null;
    groupInfo?: {
      _id: string;
      name: string;
    };
  };
  academicData: {
    currentClass?: {
      _id: string;
      name: string;
    };
    resultsHistory: any[];
    academicPerformance: {
      overallPercentage: number;
      subjectWisePerformance: {
        subjectName: string;
        averagePercentage: number;
        totalTests: number;
        bestPerformance: number;
        worstPerformance: number;
      }[];
      monthlyTrends: {
        month: number;
        year: number;
        averagePercentage: number;
      }[];
      strengths: string[];
      improvements: string[];
    };
  };
  administrativeData: {
    attendanceOverview: {
      totalDays: number;
      present: number;
      late: { total: number; informed: number; uninformed: number };
      halfDay: { total: number; informed: number; uninformed: number };
      absent: { total: number; informed: number; uninformed: number };
      scheduledOff: number;
      attendanceRate: number;
      recentPattern: {
        date: string;
        status: string;
        informationType?: string;
      }[];
    };
    lettersIssued: {
      _id: string;
      category: {
        _id: string;
        code: string;
        label: string;
        severity: 'positive' | 'neutral' | 'caution' | 'critical';
        requiresAmount: boolean;
      };
      title?: string;
      reason?: string;
      description?: string;
      amount?: number;
      paidAmount?: number;
      waivedAmount?: number;
      paymentStatus?: 'unpaid' | 'partial' | 'paid' | 'waived';
      status: 'active' | 'resolved' | 'cancelled';
      issuedAt: string;
      issuedBy: { name: string };
    }[];
    lettersSummary: {
      total: number;
      active: number;
      resolved: number;
      cancelled: number;
      byCategory: Record<string, { label: string; severity: 'positive' | 'neutral' | 'caution' | 'critical'; count: number }>;
      outstandingAmount: number;
      collectedAmount: number;
      waivedAmount: number;
    };
  };
  reportMetadata: {
    generatedAt: string;
    generatedBy: {
      _id: string;
      name: string;
      role: string;
    };
    academicYear: string;
  };
}

export interface AnalyticsDashboardData {
  summary: {
    totalStudents: number;
    totalResults: number;
    totalLetters: number;
  };
  recentActivity: {
    _id: string;
    type: string;
    studentName: string;
    generatedBy: string;
    generatedAt: string;
  }[];
}

export interface StudentsForAnalytics {
  students: {
    _id: string;
    name: string;
    rollNumber: string;
    phone: string;
    fatherName: string;
    centerId: {
      _id: string;
      name: string;
    } | null;
    groupId?: {
      _id: string;
      name: string;
    };
    enrollmentInfo: EnrollmentInfoDTO | null;
    isActive: boolean;
  }[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const getAnalyticsDashboard = async (centerId?: string): Promise<AnalyticsDashboardData> => {
  try {
    const params = new URLSearchParams();
    if (centerId) params.append('centerId', centerId);

    const response = await axios.get(
      `${API_BASE_URL}/analytics/dashboard?${params.toString()}`,
      getAuthHeaders()
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching analytics dashboard:', error);
    throw error;
  }
};

export const getStudentsForAnalytics = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  sessionId?: string;
  classId?: string;
  sectionInstanceId?: string;
}): Promise<StudentsForAnalytics> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.sessionId) queryParams.append('sessionId', params.sessionId);
    if (params.classId) queryParams.append('classId', params.classId);
    if (params.sectionInstanceId) queryParams.append('sectionInstanceId', params.sectionInstanceId);

    const response = await axios.get(
      `${API_BASE_URL}/analytics/students?${queryParams.toString()}`,
      getAuthHeaders()
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching students for analytics:', error);
    throw error;
  }
};

export const getStudentReport = async (
  studentId: string,
  options: {
    includeResults?: boolean;
    includeLetters?: boolean;
    sessionId?: string;
    classId?: string;
    sectionInstanceId?: string;
  } = {}
): Promise<StudentReportData> => {
  try {
    const params = new URLSearchParams();
    if (options.includeResults !== undefined) params.append('includeResults', options.includeResults.toString());
    if (options.includeLetters !== undefined) params.append('includeLetters', options.includeLetters.toString());
    if (options.sessionId) params.append('sessionId', options.sessionId);
    if (options.classId) params.append('classId', options.classId);
    if (options.sectionInstanceId) params.append('sectionInstanceId', options.sectionInstanceId);

    const response = await axios.get(
      `${API_BASE_URL}/analytics/student-report/${studentId}?${params.toString()}`,
      getAuthHeaders()
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching student report:', error);
    throw error;
  }
};
